// Utility functions to improve user-facing error messages
// Maps low-level network errors to friendly, localized messages

export function toFriendlyMessage(error) {
  const raw = typeof error === 'string' ? error : (error?.message || '');
  const msg = String(raw);
  const lower = msg.toLowerCase();

  // 1) Network-level failures (fetch rejection and common variants)
  if (
    msg === 'Failed to fetch' ||
    /networkerror/i.test(msg) ||
    /typeerror: failed to fetch/i.test(msg) ||
    /network request failed/i.test(lower) ||
    /load failed/i.test(lower) ||
    /econnrefused/i.test(lower)
  ) {
    return '⚠️ Không thể kết nối tới máy chủ. Hãy kiểm tra mạng hoặc đảm bảo máy chủ đang chạy, rồi thử lại sau ít phút.';
  }

  // 2) Timeouts
  if (/timeout|etimedout/i.test(lower)) {
    return '⏳ Kết nối mất quá nhiều thời gian. Vui lòng thử lại sau hoặc kiểm tra tốc độ mạng.';
  }

  // 3) CORS or SSL/TLS issues (often when backend not configured or on HTTPS)
  if (/cors/i.test(lower) || /blocked by cors policy/i.test(lower)) {
    return '🔒 Trình duyệt đang chặn yêu cầu (CORS). Vui lòng thử lại sau hoặc liên hệ quản trị để mở quyền truy cập.';
  }
  if (/ssl|tls|certificate/i.test(lower)) {
    return '🔐 Sự cố chứng chỉ bảo mật. Hãy tải lại trang hoặc thử trình duyệt khác; nếu tiếp diễn, liên hệ quản trị viên.';
  }

  // 4) DNS / name resolution
  if (/nxdomain|name not resolved|dns/i.test(lower)) {
    return '🌐 Không tìm thấy địa chỉ máy chủ. Vui lòng kiểm tra cấu hình máy chủ hoặc kết nối Internet.';
  }

  // 5) Our own generic fetch failure messages across pages
  if (/failed to fetch (tasks|goals|classes|subjects|lessons|practices|practice questions|task details|goal details|self-tests)/i.test(msg)) {
    return '🚧 Không thể tải dữ liệu từ máy chủ. Có thể máy chủ đang bận hoặc kết nối không ổn định. Vui lòng thử lại sau.';
  }

  // 6) Authorization-related
  if (/unauthorized|forbidden|401|403/i.test(lower)) {
    return '🔑 Phiên đăng nhập đã hết hạn hoặc bạn không có quyền truy cập. Vui lòng đăng nhập lại.';
  }

  // 7) Generic HTTP status patterns like "HTTP error! Status: 500"
  const httpStatusMatch = /status:\s*(\d{3})/.exec(lower);
  if (httpStatusMatch) {
    const code = parseInt(httpStatusMatch[1], 10);
    if (code >= 500) return '🛠️ Máy chủ đang bận hoặc gặp sự cố. Vui lòng thử lại sau ít phút.';
    if (code === 404) return '🔎 Không tìm thấy nội dung bạn yêu cầu. Có thể dữ liệu đã bị xóa hoặc đường dẫn không đúng.';
    if (code === 400) return '⚠️ Dữ liệu gửi lên chưa hợp lệ. Vui lòng kiểm tra lại thông tin và thử lại.';
    if (code === 429) return '⏱️ Bạn thao tác quá nhanh. Vui lòng chờ trong giây lát rồi thử lại.';
  }

  // 8) Domain-specific softening
  if (/already completed and cannot be restarted/i.test(lower)) {
    return 'Nhiệm vụ đã hoàn thành.';
  }
  if (/failed to update task status/i.test(lower)) {
    return 'Không thể cập nhật trạng thái nhiệm vụ lúc này. Vui lòng thử lại sau.';
  }

  // 9) Fallback: return original message (keeps specific errors visible for debugging)
  return msg;
}
